import { ArrowUp, Square } from 'lucide-react';
import type { ComponentProps } from 'react';

import { Button } from '@/shared/ui/shadcn/button';

export type SendButtonMode = 'send' | 'stop';

export interface SendButtonProps extends Omit<
  ComponentProps<typeof Button>,
  'children' | 'variant' | 'size' | 'type'
> {
  mode?: SendButtonMode;
}

/**
 * SendButton — circular 24x24 button for sending messages.
 *
 * Uses the Button component with default variant and circle size.
 *
 * @example
 * ```tsx
 * <SendButton onClick={handleSend} disabled={!canSend} />
 * ```
 */
export function SendButton({ mode = 'send', disabled, ...props }: SendButtonProps) {
  const isStop = mode === 'stop';
  return (
    <Button
      type={isStop ? 'button' : 'submit'}
      variant="default"
      size="circle"
      radius="pill"
      disabled={disabled}
      {...props}
    >
      {isStop ? (
        <>
          <Square className="h-4 w-4" aria-hidden="true" fill="currentColor" stroke="none" />
          <span className="sr-only">Stop</span>
        </>
      ) : (
        <>
          <ArrowUp className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">Send message</span>
        </>
      )}
    </Button>
  );
}
